import { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Box, Skeleton, LinearProgress, Typography, Stack } from '@mui/material'
import {
    CoreTypography,
    LeafIcon,
    useTheme
} from '@thriveglobal/thrive-web-leafkit'
import { VideoPlayerThumbnail } from '../../features/VideoPlayer'

interface Props {
    title?: string
    orderLabel?: string
    durationText?: string
    thumbnail?: string
    showLoading: boolean
    active?: boolean
    isComplete?: boolean
    thumbnailText?: string
    progress?: number
    rootMargin?: string
    root?: null | Element
    threshold?: number
    onPlay?: () => void
    onEnter?: (entry: IntersectionObserverEntry) => void
}

export default function CourseModuleCard({
    title,
    orderLabel,
    durationText,
    thumbnail,
    showLoading = false,
    progress = 0,
    active = false,
    isComplete = false,
    rootMargin = '0px 0px 0px 0px',
    root = null,
    threshold = 0.5,
    thumbnailText,
    onPlay,
    onEnter
}: Props) {
    const theme = useTheme()

    const intl = useIntl()

    const ref = useRef<HTMLDivElement>(null)

    const inProgress = progress > 0

    useEffect(() => {
        if (!onEnter || !ref.current) return

        const el = ref.current

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) onEnter(entry)
            },
            { rootMargin, threshold, root }
        )

        observer.observe(el)

        return () => observer.unobserve(el)
    }, [ref.current])

    return (
        <Box
            ref={ref}
            role="link"
            tabIndex={0}
            onClick={onPlay}
            sx={{
                display: 'flex',
                cursor: 'pointer'
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    height: 56,
                    width: 100,
                    flexShrink: 0
                }}
            >
                <VideoPlayerThumbnail
                    title={title}
                    onPlay={active && inProgress ? undefined : onPlay}
                    thumbnail={thumbnail}
                    showLoading={showLoading}
                />

                <Overlay
                    text={inProgress ? thumbnailText : undefined}
                    isComplete={isComplete}
                />

                {!showLoading && inProgress && (
                    <LinearProgress
                        aria-label={intl.formatMessage(
                            {
                                defaultMessage: '{percentage}% complete',
                                description: 'Percentage complete'
                            },
                            { percentage: progress }
                        )}
                        value={progress}
                        variant="determinate"
                        sx={{ position: 'absolute', width: '100%', bottom: 0 }}
                    />
                )}
            </Box>
            <Box sx={{ pl: 2 }}>
                {showLoading ? (
                    <Skeleton variant="text" width={100} height={20} />
                ) : (
                    <Stack
                        direction={'row'}
                        color={'accent.main'}
                        alignItems={'center'}
                    >
                        <LeafIcon
                            icon={'video'}
                            fontSize={'small'}
                            sx={{ mr: 0.5 }}
                        />
                        <CoreTypography
                            variant="h6"
                            variantMapping={{ h6: 'h5' }}
                            uppercase
                        >
                            {orderLabel}
                            {durationText && ` · ${durationText}`}
                        </CoreTypography>
                    </Stack>
                )}
                {showLoading ? (
                    <Skeleton variant="text" width={200} height={30} />
                ) : (
                    <Typography
                        variant="body1"
                        color={
                            active
                                ? theme.palette.primary.main
                                : theme.palette.grey[900]
                        }
                    >
                        {title}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

function Overlay({ text, isComplete }: { text?: string; isComplete: boolean }) {
    const theme = useTheme()

    if (!text) return null

    return (
        <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            position={'absolute'}
            top={0}
            p={1}
            width={'100%'}
            height={'100%'}
            sx={{
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    opacity: 0.75,
                    backgroundColor: isComplete ? 'accent.main' : 'primary.main'
                }
            }}
        >
            <CoreTypography
                variant="body2"
                uppercase
                textAlign={'center'}
                color={
                    isComplete ? 'accent.contrastText' : 'primary.contrastText'
                }
                sx={{ zIndex: 1 }}
            >
                {text}
            </CoreTypography>
        </Box>
    )
}
